import type { CardDef, Outcome, SceneBeat, SoloRunState, StoryElement, StoryPack } from '../types.js';
import { buildOutcomeKey, computeStateBucket } from '../state-bucket.js';
import { HERO_OUTCOME_MAP } from './heroes.js';
import { buildFallbackOutcome, buildTemplateOutcome } from './templates.js';
import type { LlmOutcomeClient } from '../llm/fallback.js';
import { getSceneBackgroundId } from '../story/scene-utils.js';
import { enrichVisual } from './visual-art.js';

export interface ResolveContext {
  story: StoryPack;
  sceneId: string;
  beat: SceneBeat;
  card: CardDef;
  element: StoryElement;
  state: SoloRunState;
}

export interface ResolveResult {
  outcome: Outcome;
  source: 'hero' | 'template' | 'fallback' | 'llm';
}

export function resolveOutcome(ctx: ResolveContext, llm?: LlmOutcomeClient): ResolveResult {
  const res = resolveRaw(ctx, llm);
  const scene = ctx.story.scenes.find((s) => s.id === ctx.sceneId);
  const stateKey = computeStateBucket(ctx.state).stateKey;
  return { ...res, outcome: enrichVisual(res.outcome, ctx.card, stateKey, scene?.chapterId) };
}

function resolveRaw(ctx: ResolveContext, llm?: LlmOutcomeClient): ResolveResult {
  const bucket = computeStateBucket(ctx.state);
  const key = buildOutcomeKey(ctx.story.id, ctx.beat.id, ctx.card.id, bucket.stateKey);

  const hero = HERO_OUTCOME_MAP.get(key);
  if (hero) {
    return { outcome: { ...hero, key }, source: 'hero' };
  }

  const scene = ctx.story.scenes.find((s) => s.id === ctx.sceneId);
  const backgroundId = scene ? getSceneBackgroundId(scene) : ctx.sceneId;
  const templateCtx = {
    card: ctx.card,
    beatType: ctx.beat.beatType,
    element: ctx.element,
    variables: { ...ctx.story.variables, ...ctx.state.variables },
    stateKey: bucket.stateKey,
    sceneId: ctx.sceneId,
    backgroundId,
    storyId: ctx.story.id,
    beatId: ctx.beat.id,
  };

  if (llm?.isEnabled()) {
    const llmOutcome = llm.tryResolve(key, templateCtx);
    if (llmOutcome) {
      return { outcome: llmOutcome, source: 'llm' };
    }
  }

  if (cardBeatCompatible(ctx.card, ctx.beat)) {
    return {
      outcome: buildTemplateOutcome(templateCtx),
      source: 'template',
    };
  }

  return {
    outcome: buildFallbackOutcome(templateCtx),
    source: 'fallback',
  };
}

export function cardBeatCompatible(card: CardDef, beat: SceneBeat): boolean {
  return card.beatTypes.includes(beat.beatType);
}

export function enumerateValidKeys(
  story: StoryPack,
  cards: CardDef[],
  stateKeys: string[],
): string[] {
  const keys: string[] = [];
  for (const scene of story.scenes) {
    for (const beat of scene.beats) {
      for (const card of cards) {
        if (!cardBeatCompatible(card, beat)) continue;
        for (const sk of stateKeys) {
          keys.push(buildOutcomeKey(story.id, beat.id, card.id, sk));
        }
      }
    }
  }
  return keys;
}

export const SOLO_STATE_KEYS = [
  'hope_low',
  'hope_mid',
  'hope_high',
  'dread_low',
  'dread_mid',
  'dread_high',
  'chaos_low',
  'chaos_mid',
  'chaos_high',
  'fate_low',
  'fate_mid',
  'fate_high',
];

export function coverageForStory(
  story: StoryPack,
  cards: CardDef[],
): { total: number; hero: number; templateEligible: number; missing: number } {
  const keys = enumerateValidKeys(story, cards, SOLO_STATE_KEYS);
  let hero = 0;
  let missing = 0;
  for (const key of keys) {
    if (HERO_OUTCOME_MAP.has(key)) hero++;
    else missing++;
  }
  return {
    total: keys.length,
    hero,
    templateEligible: keys.length - hero,
    missing: 0,
  };
}
